import React, { useCallback } from 'react';
import { Accordion } from '@components/Clinicians/Accordion';
import type { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import { useSessionAction } from '@hooks/useSessionAction';
import { Form } from 'antd';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useFocusWithin } from '@mantine/hooks';
import { useSessionOutcomeMutation } from '@hooks/useSessionOutcomeMutation';
import type { SessionForm } from '@components/Clinicians/session-form';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import { useClearSessionFormKeys } from '@hooks/useClearSessionForm';
import useAppointmentPrescriptions from '@hooks/useAppointmentPrescriptions';
import { useDebounceCallback } from 'usehooks-ts';

export const ActionAccordion = ({
  appointmentActionCode,
  children,
  label
}: {
  appointmentActionCode: ApptActionCodes;
  children?: React.ReactNode;
  label: string;
}) => {
  const { appointment } = useSessionAppointment();

  const form = Form.useFormInstance<SessionForm>();

  const { debouncedMutate } = useSessionOutcomeMutation();
  const { data: eligibilityChecks } = useEligibilityChecks();

  const mutateAndValidate = useCallback(() => {
    if (!form.isFieldsTouched()) return;
    debouncedMutate({ continueOnValidationError: true });
    form.validateFields();
  }, [debouncedMutate, form]);

  const debounceMutateAndValidate = useDebounceCallback(mutateAndValidate, 500);

  const { ref } = useFocusWithin({ onBlur: debounceMutateAndValidate, onFocus: debounceMutateAndValidate });

  const clearSessionFormKeys = useClearSessionFormKeys();

  const { meta, isSelected, toggle } = useSessionAction(appointmentActionCode);

  const isInvReqEnabled = !(meta?.appt_action_code === 'INVREQ' && eligibilityChecks?.VIRTUAL_ONLY);
  const isDisabled =
    !meta?.enabled || meta?.mandatory || !isInvReqEnabled || appointment.appointment_completed || appointment.read_only;

  const hasChildren = !!children || meta?.action_subtypes;
  const { updatePrescribeMedication } = useAppointmentPrescriptions();

  return (
    <Accordion
      disabled={isDisabled}
      label={meta?.appt_action_desc || label}
      required={!!(meta?.mandatory && isInvReqEnabled)}
      expanded={isSelected && isInvReqEnabled}
      actionCode={appointmentActionCode}
      onChange={(v) => {
        if (!v && hasChildren) {
          confirmationModal({
            title: 'Are you sure you want to deselect this action?',
            content: 'All data for this action will be lost once it is deselected.',
            okButtonProps: { 'data-testid': 'deselect-ok-button' },
            onOk: () => {
              toggle(v);
              clearSessionFormKeys(appointmentActionCode);
              debouncedMutate({ continueOnValidationError: true });
              if (appointmentActionCode === 'PRESCR') {
                updatePrescribeMedication(false);
              }
            },
            cancelButtonProps: { 'data-testid': 'deselect-cancel-button' }
          });
        } else {
          toggle(v);
          clearSessionFormKeys(appointmentActionCode);
          debouncedMutate({ continueOnValidationError: true });
        }
      }}
    >
      {hasChildren && <div ref={ref}>{children}</div>}
    </Accordion>
  );
};
